 
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { MARKET_DATA_DATE, MARKET_DATA_TIME_AND_DATE_WITH_TZ2 } from '../../../constants/date-time.constants';
import { ClientSidePagedResponse } from '../../../models/common-response-types';
import { Payment } from '../../../models/crm/types';
import { DEFAULT_STATUS_PAGE_RESPONSE } from '../../../models/pageable';
import { RootState } from '../../../store';
import { getPayments } from '../../../store/payment';
import { ALL_TRANSACTIONS_FILTER, DEFAULT_TRANSACTIONS_PAGE_SIZE } from '../../../store/payment/constants';
import { PaymentCall } from '../../../store/payment/types';
import { isCallStatusError, isCallStatusPending, isCallStatusReady } from '../../error-handling/StatusByCallHelpers';
import { isEmpty } from '../../typeGuards';
import { DateRangeType, NullableString } from '../../types';

type Row = {
  isPayment: true;
  timestamp: number;
  orderOrPayment: Payment;
};

type PaymentsOnlyResponse = ClientSidePagedResponse<Row> & {
  setDateRange: (range: DateRangeType) => void;
};

export const usePayments = (isUserVerified?: boolean): PaymentsOnlyResponse => {
  const dispatch = useDispatch();

  const [ page, setPage ] = useState(1);
  const [ dateRange, setDateRange ] = useState<DateRangeType>({ startDate: '', endDate: '' });

  const payments = useSelector((s: RootState) => s.payment.paymentsAll.data);

  const isPaymentsReady = useSelector((s: RootState) => isCallStatusReady(PaymentCall.getPaymentsAll, s.payment.statusByCall));
  const isPaymentLoading = useSelector((s: RootState) => isCallStatusPending(PaymentCall.getPaymentsAll, s.payment.statusByCall));
  const hasPaymentError = useSelector((s: RootState) => isCallStatusError(PaymentCall.getPaymentsAll, s.payment.statusByCall));

  useEffect(() => {
    if (!isUserVerified || isPaymentLoading || isPaymentsReady || hasPaymentError) return;
    dispatch(getPayments(ALL_TRANSACTIONS_FILTER));
  }, [ dispatch, isUserVerified, isPaymentLoading, isPaymentsReady, hasPaymentError ]);

  const content: Row[] = useMemo(() => (payments || [])
    .map(p => ({
      isPayment: true as const,
      timestamp: moment(p.create_date, MARKET_DATA_TIME_AND_DATE_WITH_TZ2).valueOf(),
      orderOrPayment: p,
    }))
    .sort((a, b) => b.timestamp - a.timestamp), [ payments ]);

  const filterContentBy = (
    pageNumber: number,
    startDateStr?: NullableString,
    endDateStr?: NullableString,
  ) => {
    const endIndex = DEFAULT_TRANSACTIONS_PAGE_SIZE * pageNumber;
    let pool = content;

    if (startDateStr) {
      const startTs = moment(startDateStr, MARKET_DATA_DATE).utc().valueOf();
      const endTs = moment(endDateStr, MARKET_DATA_DATE).utc().add(1, 'days').valueOf();
      pool = content.filter(row => row.timestamp >= startTs && row.timestamp <= endTs);
    }

    result.filteredContent = pool.length > endIndex ? pool.slice(0, endIndex) : pool;
  };

  const changePage = (n: number) => {
    filterContentBy(n, dateRange.startDate, dateRange.endDate);
    setPage(n);
  };

  const changeDate = (range: DateRangeType) => {
    filterContentBy(1, range.startDate, range.endDate);
    setPage(1);
    setDateRange(range);
  };

  const result = {
    ...DEFAULT_STATUS_PAGE_RESPONSE,
    number: page,
    setPage: changePage,
    dateRange,
    setDateRange: changeDate,
    content,
    filteredContent: [],
    hasMorePages: false,
  } as unknown as PaymentsOnlyResponse;

  filterContentBy(page, dateRange.startDate, dateRange.endDate);

  result.totalElements = content.length;
  result.numberOfElements = result.filteredContent.length;
  result.hasMorePages = !isEmpty(content)
    && result.totalElements > result.numberOfElements
    && result.filteredContent.length >= DEFAULT_TRANSACTIONS_PAGE_SIZE;

  return result;
};
